import type { TaskFilters } from "./filters";
import type { Task } from "./types";

/**
 * What both tabs derive the same way.
 *
 * The two tabs are separate routes with separate reads and separate models
 * (`plans-model.ts`, `tasks-model.ts`), but they are still two readings of one
 * warehouse - so what they do share has to be one answer in one place rather
 * than the same rule written slightly differently on each.
 *
 * Whether a task survives the filter bar is not in here, and no longer anywhere
 * on this side: every filter is a query param on both tabs, so the rows a read
 * hands back are the answer rather than a set still to be narrowed.
 *
 * Kept pure and kept out of the components. Nothing here knows where a task came
 * from, or which tab is asking - still true, though a row's progress is now a
 * union rather than a bare number, because a planned task does not have one.
 */

/**
 * A row's progress, or the absence of the concept.
 *
 * `not-released` is not zero progress. Zero is a claim about a task somebody
 * could have started and has not; a task nobody released has no progress to
 * report at all, and a progress bar at 0% in that cell reads as the first of
 * those while meaning the second - a manager would read a freshly generated,
 * unreleased task the same as one sitting untouched in the pool.
 */
export type TaskProgress =
  | { kind: "known"; picksDone: number; picksTotal: number; percent: number }
  | { kind: "not-released" };

/** A task as one table row. Both tabs draw these. */
export interface TaskRow {
  task: Task;
  progress: TaskProgress;
}

/**
 * A search is in the boxes, so the scope chips have been overridden.
 *
 * Searching for a task id and being shown nothing because the task is in a scope
 * you were not looking at is indistinguishable from the task not existing. So a
 * search widens to every status, on both tabs, and the screen says so.
 */
export function hasSearch(filters: TaskFilters): boolean {
  return filters.task.kind === "query" || filters.route.kind === "query";
}

export function toRow(task: Task): TaskRow {
  if (task.kind === "planned") {
    return { task, progress: { kind: "not-released" } };
  }

  const picksTotal = task.lineCount;
  const picksDone = task.picksDone;

  return {
    task,
    progress: {
      kind: "known",
      picksDone,
      picksTotal,
      percent:
        picksTotal === 0 ? 0 : Math.round((picksDone / picksTotal) * 100),
    },
  };
}

/**
 * Whether `POST /task-assignment` would accept this task: out on the floor, in the pool, never held.
 *
 * The service 409s on anything else (a task already assigned, claimed or completed, or on a plan
 * that is not activated), so the table disables the checkbox rather than letting the manager find
 * out from a toast. `"unassigned"` is the domain word for the wire's `pending` (`wire.ts statusOf`).
 * A planned task has no activation at all, so it is never assignable.
 */
export function isAssignable(task: Task): boolean {
  return (
    task.kind === "activated" &&
    task.status === "unassigned" &&
    task.selectorId === null
  );
}

/**
 * Whether `POST /task-assignment` `reassign_selector` would accept this task: held by a selector
 * who has not claimed it yet. The service 409s on a pooled, claimed or completed task.
 */
export function isReassignable(task: Task): boolean {
  return (
    task.kind === "activated" &&
    task.status === "assigned" &&
    task.selectorId !== null
  );
}

/**
 * The one write a ticked task can take part in. `assign` and `reassign` are different `kind`s on
 * the wire, so a selection holds one or the other, never both.
 */
export type AssignMode = "assign" | "reassign";

/** Null: the task can be ticked for neither. */
export function assignModeOf(task: Task): AssignMode | null {
  if (isAssignable(task)) return "assign";
  if (isReassignable(task)) return "reassign";
  return null;
}
